<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex flex-wrap align-items-center justify-content-between">
        <b-card-title class="mb-0">
          مسارات عمل المرفقات
        </b-card-title>
        <div>
          <b-button
            variant="outline-secondary"
            class="mr-1 mb-50"
            @click="openReorderModal"
          >
            <feather-icon
              icon="ShuffleIcon"
              class="mr-50"
            />
            <span class="align-middle">إعادة الترتيب</span>
          </b-button>
          <b-button
            variant="primary"
            class="mb-50"
            @click="openCreateModal"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            <span class="align-middle">إضافة مسار</span>
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              :label="$t('common.search')"
              label-for="std-search"
            >
              <b-form-input
                id="std-search"
                v-model="filters.search"
                placeholder="بحث بالاسم"
                @input="onSearchInput"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              :label="$t('common.status')"
              label-for="std-status"
            >
              <v-select
                v-model="filters.is_active"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                :placeholder="$t('common.all')"
                @input="fetchData"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <b-form-group
              label="عدد العناصر"
              label-for="std-per-page"
            >
              <v-select
                v-model="filters.per_page"
                :options="perPageOptions"
                :reduce="option => option.value"
                label="label"
                @input="handlePerPageChange"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <p class="text-muted small mb-0">
          أقسام مسار العمل المستخدمة عند ربط أنواع المرفقات بأنواع الخدمات (ليست الأقسام الإدارية).
        </p>
      </b-card-body>
    </b-card>

    <b-card
      no-body
      class="mt-2"
    >
      <b-table
        :items="items"
        :fields="tableColumns"
        :busy="load"
        responsive
        show-empty
        empty-text="لا توجد بيانات"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('common.loading') }}</strong>
          </div>
        </template>

        <template #cell(color)="data">
          <span
            v-if="data.item.color"
            class="d-inline-flex align-items-center"
          >
            <span
              class="rounded border d-inline-block mr-50"
              style="width: 22px; height: 22px; vertical-align: middle;"
              :style="{ backgroundColor: safeColor(data.item.color) || '#ccc' }"
            />
            <small class="text-muted">{{ data.item.color }}</small>
          </span>
          <span
            v-else
            class="text-muted"
          >—</span>
        </template>

        <template #cell(is_active)="data">
          <b-badge
            :variant="data.item.is_active ? 'success' : 'danger'"
            pill
          >
            {{ data.item.is_active ? $t('common.active') : $t('common.inactive') }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              v-b-tooltip.hover
              :title="data.item.is_active ? 'تعطيل' : 'تفعيل'"
              variant="flat-secondary"
              size="sm"
              @click="onToggleStatus(data.item)"
            >
              <feather-icon :icon="data.item.is_active ? 'EyeOffIcon' : 'EyeIcon'" />
            </b-button>
            <b-button
              variant="flat-info"
              size="sm"
              @click="openEditModal(data.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              variant="flat-danger"
              size="sm"
              @click="confirmDelete(data.item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </template>
      </b-table>

      <b-card-body class="d-flex justify-content-between align-items-center pt-0">
        <div>
          <span class="text-muted">{{ $t('common.showing') }} {{ meta.from || 0 }} {{ $t('common.to') }} {{ meta.to || 0 }} {{ $t('common.of') }} {{ meta.total || 0 }} {{ $t('common.entries') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="meta.total || 0"
          :per-page="filters.per_page"
          first-number
          last-number
          prev-class="prev-item"
          next-class="next-item"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </b-card-body>
    </b-card>

    <b-modal
      v-model="showFormModal"
      :title="isEdit ? 'تعديل مسار العمل' : 'إضافة مسار عمل'"
      :ok-title="isEdit ? $t('common.update') : $t('common.create')"
      :cancel-title="$t('common.cancel')"
      :ok-disabled="submitting"
      @ok="submitForm"
      @hidden="resetForm"
    >
      <b-form @submit.stop.prevent="submitForm">
        <b-form-group
          label="الاسم *"
          label-for="std-name"
        >
          <b-form-input
            id="std-name"
            v-model.trim="form.name"
            maxlength="255"
            :state="errors.name ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.name">
            {{ errors.name[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="الترتيب"
          label-for="std-sort"
        >
          <b-form-input
            id="std-sort"
            v-model.number="form.sort_order"
            type="number"
            min="0"
            :state="errors.sort_order ? false : null"
          />
          <b-form-invalid-feedback v-if="errors.sort_order">
            {{ errors.sort_order[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          label="اللون (اختياري، حتى 32 حرفًا)"
          label-for="std-color-hex"
        >
          <div class="d-flex align-items-center flex-wrap">
            <b-form-input
              id="std-color-picker"
              v-model="form.color"
              type="color"
              class="mr-1 mb-50"
              style="width: 52px; height: 38px; padding: 2px;"
              :state="errors.color ? false : null"
            />
            <b-form-input
              id="std-color-hex"
              v-model.trim="form.color"
              maxlength="32"
              class="flex-grow-1 mb-50"
              placeholder="#06284D"
              :state="errors.color ? false : null"
            />
          </div>
          <b-form-invalid-feedback
            v-if="errors.color"
            class="d-block"
          >
            {{ errors.color[0] }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-checkbox
          v-model="form.is_active"
          switch
        >
          {{ $t('common.active') }}
        </b-form-checkbox>
      </b-form>
    </b-modal>

    <b-modal
      v-model="showReorderModal"
      title="إعادة ترتيب مسارات العمل"
      ok-title="حفظ الترتيب"
      :cancel-title="$t('common.cancel')"
      size="lg"
      :ok-disabled="reorderSubmitting || !reorderItems.length"
      @ok="submitReorder"
      @shown="onReorderModalShown"
      @hidden="onReorderModalHidden"
    >
      <p class="text-muted small">
        اسحب العناصر للترتيب المطلوب، ثم احفظ. يجب أن يشمل الترتيب جميع المسارات الحالية.
      </p>
      <div
        v-if="reorderLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <draggable
        v-else
        v-model="reorderItems"
        handle=".std-drag-handle"
        tag="div"
        class="list-group"
      >
        <div
          v-for="el in reorderItems"
          :key="el.id"
          class="list-group-item d-flex align-items-center"
        >
          <feather-icon
            icon="MenuIcon"
            class="std-drag-handle cursor-move mr-1 text-muted"
          />
          <span class="flex-grow-1">{{ el.name }}</span>
          <b-badge
            v-if="!el.is_active"
            variant="secondary"
            pill
            class="ml-1"
          >معطّل</b-badge>
        </div>
      </draggable>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
  BModal,
  BForm,
  BFormCheckbox,
  BFormInvalidFeedback,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ServiceTypeAttachmentDepartmentsManagement',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    BModal,
    BForm,
    BFormCheckbox,
    BFormInvalidFeedback,
    vSelect,
    draggable,
  },
  data() {
    return {
      filters: {
        search: '',
        is_active: null,
        per_page: 15,
      },
      currentPage: 1,
      tableColumns: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'الاسم' },
        { key: 'color', label: 'اللون' },
        { key: 'sort_order', label: 'الترتيب' },
        { key: 'is_active', label: this.$t('common.status') },
        { key: 'actions', label: this.$t('common.actions') },
      ],
      statusOptions: [
        { label: this.$t('common.all'), value: null },
        { label: this.$t('common.active'), value: true },
        { label: this.$t('common.inactive'), value: false },
      ],
      perPageOptions: [
        { label: '15', value: 15 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
      ],
      showFormModal: false,
      isEdit: false,
      currentId: null,
      submitting: false,
      form: {
        name: '',
        sort_order: 0,
        color: '',
        is_active: true,
      },
      errors: {},
      showReorderModal: false,
      reorderItems: [],
      reorderLoading: false,
      reorderSubmitting: false,
      searchDebounceTimer: null,
    }
  },
  computed: {
    ...mapState('serviceTypeAttachmentDepartments', ['items', 'meta', 'load']),
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
    }
  },
  methods: {
    ...mapActions('serviceTypeAttachmentDepartments', [
      'list',
      'get',
      'create',
      'update',
      'delete',
      'toggleStatus',
      'reorder',
    ]),
    safeColor(color) {
      if (!color || typeof color !== 'string') return null
      const t = color.trim()
      if (/^#[0-9A-Fa-f]{3,8}$/.test(t)) return t
      if (/^[0-9A-Fa-f]{3}$/.test(t)) return `#${t}`
      if (/^[0-9A-Fa-f]{6}$/.test(t)) return `#${t}`
      if (/^rgba?\(\s*[\d.%\s,]+\s*\)$/i.test(t)) return t
      if (/^hsla?\(\s*[\d.%\s,]+\s*\)$/i.test(t)) return t
      return null
    },
    onSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null
        this.currentPage = 1
        this.fetchData()
      }, 500)
    },
    async fetchData() {
      const params = {
        page: this.currentPage,
        per_page: this.filters.per_page,
      }
      if (this.filters.search) params.search = this.filters.search
      if (this.filters.is_active !== null) params.is_active = this.filters.is_active
      try {
        await this.list(params)
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر تحميل البيانات')
      }
    },
    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },
    handlePerPageChange() {
      this.currentPage = 1
      this.fetchData()
    },
    openCreateModal() {
      this.resetForm()
      this.isEdit = false
      this.currentId = null
      this.showFormModal = true
    },
    async openEditModal(item) {
      this.resetForm()
      this.isEdit = true
      this.currentId = item.id
      this.showFormModal = true
      try {
        const response = await this.get(item.id)
        const data = response?.data || item
        this.form = {
          name: data.name || '',
          sort_order: Number.isFinite(Number(data.sort_order)) ? Number(data.sort_order) : 0,
          color: (data.color && String(data.color).trim()) || '',
          is_active: data.is_active !== false,
        }
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر تحميل العنصر')
      }
    },
    buildPayload() {
      const payload = {
        name: this.form.name,
        sort_order: this.form.sort_order ?? 0,
        is_active: this.form.is_active,
      }
      const c = (this.form.color || '').trim()
      if (c) payload.color = c.slice(0, 32)
      return payload
    },
    async submitForm(event) {
      if (event) event.preventDefault()
      this.submitting = true
      this.errors = {}
      try {
        const payload = this.buildPayload()
        if (this.isEdit && this.currentId) {
          await this.update({ id: this.currentId, data: payload })
          this.showToast('success', 'تم التحديث بنجاح')
        } else {
          await this.create(payload)
          this.showToast('success', 'تم الإنشاء بنجاح')
        }
        this.showFormModal = false
        await this.fetchData()
      } catch (error) {
        if (error?.response?.status === 422) {
          this.errors = error?.response?.data?.errors || {}
        }
        this.showToast('error', error?.response?.data?.message || 'تعذر حفظ البيانات')
      } finally {
        this.submitting = false
      }
    },
    async onToggleStatus(item) {
      try {
        await this.toggleStatus(item.id)
        this.showToast('success', 'تم تحديث الحالة')
        await this.fetchData()
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر تحديث الحالة')
      }
    },
    confirmDelete(item) {
      this.$swal({
        title: 'تأكيد الحذف',
        text: 'هل أنت متأكد من حذف هذا المسار؟',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('common.yes'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (!result.isConfirmed) return
        try {
          await this.delete(item.id)
          this.showToast('success', 'تم الحذف بنجاح')
          await this.fetchData()
        } catch (error) {
          const message = error?.response?.data?.message || 'تعذر حذف العنصر'
          if (this.isLinkedDeleteError(error)) {
            this.showToast('warning', message)
            return
          }
          this.showToast('error', message)
        }
      })
    },
    isLinkedDeleteError(error) {
      const message = (error?.response?.data?.message || '').toLowerCase()
      return message.includes('linked')
        || message.includes('service type')
        || message.includes('cannot delete department')
        || message.includes('مرتبط')
    },
    resetForm() {
      this.errors = {}
      this.form = {
        name: '',
        sort_order: 0,
        color: '',
        is_active: true,
      }
    },
    openReorderModal() {
      this.showReorderModal = true
    },
    onReorderModalHidden() {
      this.reorderItems = []
      this.reorderLoading = false
    },
    async onReorderModalShown() {
      this.reorderLoading = true
      this.reorderItems = []
      try {
        // eslint-disable-next-line import/no-cycle
        const { default: serviceTypeAttachmentDepartmentsApi } = await import(
          '@/api/settings/serviceTypeAttachmentDepartments'
        )
        const response = await serviceTypeAttachmentDepartmentsApi.list({ all: true })
        const payload = response?.data
        let rows = []
        if (Array.isArray(payload)) {
          rows = payload
        } else if (Array.isArray(payload?.data)) {
          rows = payload.data
        }
        this.reorderItems = [...rows].sort((a, b) => {
          const sa = Number(a.sort_order) || 0
          const sb = Number(b.sort_order) || 0
          if (sa !== sb) return sa - sb
          return (a.id || 0) - (b.id || 0)
        })
      } catch (e) {
        this.showToast('error', e?.response?.data?.message || 'تعذر تحميل القائمة')
        this.showReorderModal = false
      } finally {
        this.reorderLoading = false
      }
    },
    async submitReorder(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.reorderSubmitting = true
      try {
        const order = this.reorderItems.map(r => r.id).filter(id => id != null)
        await this.reorder(order)
        this.showToast('success', 'تم حفظ الترتيب')
        this.showReorderModal = false
        this.currentPage = 1
        await this.fetchData()
      } catch (error) {
        this.showToast('error', error?.response?.data?.message || 'تعذر حفظ الترتيب')
      } finally {
        this.reorderSubmitting = false
      }
    },
    showToast(type, text) {
      const map = {
        success: { title: this.$t('common.success'), icon: 'CheckIcon', variant: 'success' },
        warning: { title: this.$t('common.error'), icon: 'AlertTriangleIcon', variant: 'warning' },
        error: { title: this.$t('common.error'), icon: 'AlertTriangleIcon', variant: 'danger' },
      }
      const config = map[type] || map.error
      this.$toast({
        component: ToastificationContent,
        props: {
          ...config,
          text,
        },
      })
    },
  },
}
</script>

<style scoped>
.cursor-move {
  cursor: move;
}
</style>
